import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout';
import { SpecialistCard } from '@/components/domain';
import { EmptyState, ErrorState, Spinner } from '@/components/atoms';
import { useBackButton } from '@/hooks';
import { favorites } from '@/api';
import type { Favorite } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function FavoritesPage(): JSX.Element {
  useBackButton();
  const [items, setItems] = useState<Favorite[] | null>(null);
  const [errored, setErrored] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setErrored(false);
    setItems(null);
    (async () => {
      try {
        const data = await favorites.fetchFavorites();
        if (cancelled) return;
        // F-170 B1.4: backend may return either `{ specialist: {...} }` (new
        // contract) or a legacy `{ specialist_slug }`-only row. Filter out
        // any row that doesn't carry a renderable specialist so the page
        // never crashes on `f.specialist.slug` when the nested object is
        // missing. The "real" fix lives backend-side in B0.7.
        const usable = (data.results ?? []).filter(
          (f): f is Favorite => Boolean(f?.specialist?.slug),
        );
        setItems(usable);
      } catch (err) {
        logger.warn('Favorites fetch failed', err);
        if (!cancelled) {
          setItems([]);
          setErrored(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (items === null) {
    return (
      <Layout title="Закладки">
        <Spinner center />
      </Layout>
    );
  }
  if (errored && items.length === 0) {
    return (
      <Layout title="Закладки">
        <ErrorState onRetry={() => setReloadKey((k) => k + 1)} />
      </Layout>
    );
  }
  if (items.length === 0) {
    return (
      <Layout title="Закладки">
        <EmptyState
          icon="favorite"
          message="Жодної закладки. Тапни на сердечко на профілі — додасться сюди."
          action={{ label: 'Шукати', to: '/search' }}
        />
      </Layout>
    );
  }

  return (
    <Layout title="Закладки">
      <div className={styles.list}>
        {items.map((f) => (
          <SpecialistCard key={f.specialist.slug} specialist={f.specialist} />
        ))}
      </div>
    </Layout>
  );
}
